import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ActionResult, ActionRunner, AdminAction, AdminActionField, isDispatchedTimeout } from './AdminTools.actions'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'

import Button from 'components/common/Button/Button'
import { MUICheckbox } from 'components/common/MUICheckBox'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { MUIInputField } from 'components/common/MUIInputField'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import Typography from '@mui/material/Typography'
import axios from 'axios'
import classnames from 'classnames'
import styles from './AdminTools.module.sass'
import { useActionPopup } from 'utils/hooks'
import { useMutation } from '@tanstack/react-query'

interface Props {
  action: AdminAction
  run: ActionRunner
}

const initialValues = (fields?: AdminActionField[]): Record<string, string> =>
  Object.fromEntries((fields ?? []).map(field => [field.key, field.defaultValue ?? '']))

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const isNotRunnable = (
  action: AdminAction,
  values: Record<string, string>,
  files: Record<string, File>
): boolean =>
  (action.fields ?? []).some(field => {
    if (field.type === 'file') return field.required && !files[field.key]
    const value = values[field.key]?.trim()
    if (field.type === 'uuid' && value && !UUID_PATTERN.test(value)) return true
    return field.required && !value
  })

const formatData = (result: ActionResult): string =>
  result.data === '' || result.data == null ? `OK (${result.status})` : JSON.stringify(result.data, null, 2)

const inputElementType = (type: AdminActionField['type']): string =>
  type === 'number' ? 'number' : type === 'email' ? 'email' : 'text'

export const AdminToolsActionCard: React.FC<Props> = ({ action, run }) => {
  const { showConfirm } = useActionPopup()
  const [values, setValues] = useState<Record<string, string>>(() => initialValues(action.fields))
  const [files, setFiles] = useState<Record<string, File>>({})
  const [elapsed, setElapsed] = useState(0)
  const [durationMs, setDurationMs] = useState(0)
  const startedAt = useRef(0)

  const mutation = useMutation<ActionResult, unknown, Record<string, string>>({
    mutationFn: submitted => run(action, submitted, files),
    onSettled: () => setDurationMs(Date.now() - startedAt.current),
  })

  useEffect(() => {
    if (!mutation.isPending) return
    const interval = window.setInterval(() => setElapsed(Math.round((Date.now() - startedAt.current) / 1000)), 1000)
    return () => window.clearInterval(interval)
  }, [mutation.isPending])

  const disabled = useMemo(() => isNotRunnable(action, values, files), [action, values, files])

  const setValue = (key: string, value: string) => setValues(prev => ({ ...prev, [key]: value }))

  const setFile = (key: string, file: File | undefined) =>
    setFiles(prev => {
      const { [key]: _removed, ...rest } = prev
      return file ? { ...rest, [key]: file } : rest
    })

  const toggleMulti = (key: string, option: string) => {
    const current = values[key] ? values[key].split(',') : []
    const next = current.includes(option) ? current.filter(item => item !== option) : [...current, option]
    setValue(key, next.join(','))
  }

  const onRun = async () => {
    if (action.confirm && !(await showConfirm(action.confirm))) return
    startedAt.current = Date.now()
    setElapsed(0)
    mutation.mutate(values)
  }

  const renderField = (field: AdminActionField) => {
    if (field.type === 'select') {
      return (
        <MUIDropdown
          sx={{ width: '100%' }}
          button={(isOpen, openAction) => (
            <MUIDropdownInput
              readOnly
              required={field.required}
              label={field.label}
              type={DropdownInputTypes.SELECTOR}
              placeholder={field.placeholder}
              value={values[field.key] || ''}
              onClick={openAction}
            />
          )}
        >
          {(field.options ?? []).map(option => (
            <MUIDropdownItem key={option} onClick={() => setValue(field.key, option)}>
              <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                {option === '' ? '— none —' : option}
              </Typography>
            </MUIDropdownItem>
          ))}
        </MUIDropdown>
      )
    }
    if (field.type === 'file') {
      return (
        <>
          <Typography variant="text-sm" color={GRAY_700}>{field.label}{field.required && ' *'}</Typography>
          <input
            type="file"
            accept={field.accept}
            aria-label={field.label}
            onClick={event => {
              event.currentTarget.value = ''
              setFile(field.key, undefined)
            }}
            onChange={event => setFile(field.key, event.target.files?.[0])}
          />
        </>
      )
    }
    if (field.type === 'multiSelect') {
      const selected = values[field.key] ? values[field.key].split(',') : []
      return (
        <>
          <Typography variant="text-sm" color={GRAY_700}>{field.label}{field.required && ' *'}</Typography>
          <div className={styles.checkboxRow}>
            {(field.options ?? []).map(option => (
              <MUICheckbox
                key={option}
                label={option}
                checked={selected.includes(option)}
                onChange={() => toggleMulti(field.key, option)}
              />
            ))}
          </div>
        </>
      )
    }
    return (
      <MUIInputField
        label={field.label}
        required={field.required}
        placeholder={field.placeholder}
        isMultiline={field.type === 'list'}
        inputElementType={inputElementType(field.type)}
        value={values[field.key]}
        onChange={event => setValue(field.key, event.target.value)}
      />
    )
  }

  const renderOutcome = () => {
    if (mutation.isSuccess) {
      return (
        <div className={classnames(styles.outcome, styles.outcomeSuccess)}>
          <div className={styles.outcomeMeta}>✓ Success · {durationMs}ms</div>
          <pre className={styles.outcomePre}>{formatData(mutation.data)}</pre>
        </div>
      )
    }
    if (!mutation.isError) return null

    const error = mutation.error
    const axiosError = axios.isAxiosError(error) ? error : undefined
    if (action.longRunning && axiosError && isDispatchedTimeout(axiosError)) {
      return (
        <div className={classnames(styles.outcome, styles.outcomeDispatched)}>
          <div className={styles.outcomeMeta}>⧗ Dispatched · {durationMs}ms</div>
          <pre className={styles.outcomePre}>
            Dispatched. No response within the gateway timeout — this operation continues running server-side
            and is idempotent. Confirm completion via logs or a follow-up read action.
          </pre>
        </div>
      )
    }
    const status = axiosError?.response?.status
    const body = axiosError?.response?.data
    const message = error instanceof Error ? error.message : String(error)
    return (
      <div className={classnames(styles.outcome, styles.outcomeError)}>
        <div className={styles.outcomeMeta}>✕ Error · {durationMs}ms</div>
        <pre className={styles.outcomePre}>
          {(status ? `${status} — ` : '') + message + (body ? `\n${JSON.stringify(body, null, 2)}` : '')}
        </pre>
      </div>
    )
  }

  const title = (
    <div className={styles.cardHeader}>
      <span className={classnames(styles.method, styles[`method_${action.method}`])}>{action.method}</span>
      <Typography variant="text-md" color={GRAY_900} fontWeight="medium">{action.label}</Typography>
    </div>
  )

  return (
    <SectionedBorderBox title={title} description={action.description}>
      <div className={styles.cardBody}>
        {(action.fields ?? []).map(field => (
          <div key={field.key} className={styles.field}>{renderField(field)}</div>
        ))}
        <div className={styles.runRow}>
          <Button
            type="primary"
            height="thin"
            isLoading={mutation.isPending}
            disabled={disabled || mutation.isPending}
            onClick={onRun}
          >
            Run
          </Button>
          {mutation.isPending && <span className={styles.elapsed}>running… {elapsed}s</span>}
        </div>
        {renderOutcome()}
      </div>
    </SectionedBorderBox>
  )
}
